import classNames from "classnames";
import { StaticChip } from "components/ui/chips/StaticChip";
import { FavoriteIcon } from "components/ui/Icons/FavoriteIcon";
import { Tooltip } from "components/ui/Tooltip";
import { Typography } from "components/ui/Typography";
import { useRouteAccess } from "hooks/useRouteAccess";
import isNil from "lodash/isNil";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, matchPath, useLocation } from "react-router";
import { routes } from "routes/routes";
import { flattenRoutes } from "utils/router/utils";
import { requirePropertyOf } from "utils/security";

import { useStyles } from "./styles";

const ICON_SIZE_PX = 24;

interface IProps {
  Icon: IconComponent;
  indication?: "beta" | "new";
  minimized: boolean;
  routeKey: keyof typeof routes;
  text: string;
}

export const TabOption: FC<IProps> = ({ Icon, indication, minimized, routeKey, text }) => {
  const classes = useStyles();
  const { t } = useTranslation(undefined, { keyPrefix: "navigation.indications" });

  const location = useLocation();

  const route = requirePropertyOf(routes, routeKey);

  const flattenedRoutes = useMemo(
    () => (route ? flattenRoutes({ routes: { [routeKey]: route } }) : []),
    [route, routeKey],
  );

  const active = useMemo(
    () =>
      flattenedRoutes.some((route) => {
        const matches = Array.isArray(route.matches) ? route.matches : [route.matches];
        return matches.some((path) => !isNil(matchPath(path, location.pathname)));
      }),
    [flattenedRoutes, location.pathname],
  );

  const goToUrlMatch = useMemo(() => matchPath(location.pathname, route.main), [location.pathname, route.main]);

  const { allowed, loading } = useRouteAccess(route);
  if (loading || !allowed) return null;

  const canClick = goToUrlMatch === null;

  const classString = classNames(classes.option, { [classes.active]: active });

  const textElement = (
    <Typography noWrap variant="body_reg">
      {text}
    </Typography>
  );

  const content = (
    <>
      <div className={classes.title}>
        <Icon size={ICON_SIZE_PX} />
        <div className={classNames(classes.text, { [classes.minimized]: minimized })}>{textElement}</div>
      </div>

      {indication && (
        <StaticChip size="small" SuffixIcon={FavoriteIcon} variant="regular">
          {t(indication)}
        </StaticChip>
      )}
    </>
  );

  return (
    <Tooltip
      className={classes.tooltip}
      content={textElement}
      placement="right"
      visible={!minimized ? false : undefined}
    >
      <div className={classNames(classes.container, { [classes.minimized]: minimized })}>
        {canClick
          ? (
              <Link className={classString} to={route.main}>
                {content}
              </Link>
            )
          : (
              <div className={classString}>{content}</div>
            )}
      </div>
    </Tooltip>
  );
};
